import { useState, useEffect } from 'react';
import * as qaSpecServices from 'services/qaSpec';

/**
 * useDynamicLookups
 * - demo mode: returns built-in hardcoded values (for demo).
 * - live mode: calls qaSpecServices.getDynamicLookUps for each fieldType.
 */
export const useDynamicLookups = ({ demo = undefined, fallbackToDemo = false } = {}) => {
  const isDemoMode = demo !== undefined ? Boolean(demo) : false;

  const demoDefaults = {
    recyclableType: [
      { id: 130, fieldType: 'RecyclableType', code: 'Aluminium', displayName: 'Aluminium', description: 'Aluminium recyclable', sortOrder: 10 },
      { id: 131, fieldType: 'RecyclableType', code: 'Cardboard', displayName: 'Cardboard', description: 'Cardboard recyclable', sortOrder: 20 },
    ],
    packagingType: [
      { id: 46, fieldType: 'PackagingType', code: 'Tube', displayName: 'Tube', description: 'Plastic or metal tube', sortOrder: 460 },
      { id: 47, fieldType: 'PackagingType', code: 'Wire', displayName: 'Wire', description: 'Wire packaging', sortOrder: 470 },
    ],
    materialUOM: [],
    packagingMaterial: [
      { id: 48, fieldType: 'MaterialType', code: 'Aluminum', displayName: 'Aluminum', description: 'Aluminum metal', sortOrder: 10 },
      { id: 49, fieldType: 'MaterialType', code: 'ColouredGlass', displayName: 'Coloured Glass', description: 'Coloured glass', sortOrder: 20 },
      { id: 50, fieldType: 'MaterialType', code: 'CorrugatedPaper', displayName: 'Corrugated Paper', description: 'Corrugated paper', sortOrder: 30 },
      { id: 51, fieldType: 'MaterialType', code: 'Glass', displayName: 'Glass', description: 'Glass material', sortOrder: 40 },
      { id: 52, fieldType: 'MaterialType', code: 'HighDensityPolyethyleneHDPE', displayName: 'High Density Polyethylene (HDPE)', description: 'High density polyethylene - #2', sortOrder: 50 },
      { id: 53, fieldType: 'MaterialType', code: 'LowdensitypolyethyleneLDPE', displayName: 'Low-density polyethylene (LDPE)', description: 'Low-density polyethylene - #4', sortOrder: 60 },
    ],
  };

  const [recyclableType, setRecyclableType] = useState(demoDefaults.recyclableType);
  const [packagingType, setPackagingType] = useState(demoDefaults.packagingType);
  const [materialUOM, setMaterialUOM] = useState(demoDefaults.materialUOM);
  const [packagingMaterial, setPackagingMaterial] = useState(demoDefaults.packagingMaterial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const toArray = (res) => {
      if (!res) return [];
      if (Array.isArray(res)) return res;
      if (res.data && Array.isArray(res.data)) return res.data;
      return [];
    };

    const fetchAllLookups = async () => {
      if (isDemoMode) {
        // demo: return defaults immediately
        if (mounted) {
          setRecyclableType(demoDefaults.recyclableType);
          setPackagingType(demoDefaults.packagingType);
          setMaterialUOM(demoDefaults.materialUOM);
          setPackagingMaterial(demoDefaults.packagingMaterial);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const [
          recyclableRes,
          packagingTypeRes,
          packagingMaterialRes,
        ] = await Promise.all([
          qaSpecServices.getDynamicLookUps({ fieldType: 'RecyclableType' }),
          qaSpecServices.getDynamicLookUps({ fieldType: 'PackagingType' }),
          qaSpecServices.getDynamicLookUps({ fieldType: 'MaterialType' }),
        ]);

        if (!mounted) return;

        setRecyclableType(toArray(recyclableRes));
        setPackagingType(toArray(packagingTypeRes));
        setPackagingMaterial(toArray(packagingMaterialRes));
      } catch (err) {
        // on error, either fallback to demo or return empty arrays based on fallbackToDemo
        // keep a console.error for debugging
        // eslint-disable-next-line no-console
        console.error('useDynamicLookups: fetch error', err);
        if (mounted) {
          if (fallbackToDemo) {
            setRecyclableType(demoDefaults.recyclableType);
            setPackagingType(demoDefaults.packagingType);
            setMaterialUOM(demoDefaults.materialUOM);
            setPackagingMaterial(demoDefaults.packagingMaterial);
          } else {
            setRecyclableType([]);
            setPackagingType([]);
            setMaterialUOM([]);
            setPackagingMaterial([]);
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAllLookups();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoMode, fallbackToDemo]);

  return {
    recyclableType,
    packagingType,
    materialUOM,
    packagingMaterial,
    loading,
    isDemo: Boolean(isDemoMode),
  };
};